import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cs from 'classnames';
import { Button, DropdownMenu } from 'components/atoms';
import Popup from 'components/molecules/popup';
import { useComponentVisible, useFormikContractHelpers } from 'hooks';
import i18next from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import {
  PiCaretDown,
  PiCassetteTape,
  PiDotsSixVerticalBold,
  PiDotsThreeVerticalBold,
  PiHeadphones,
  PiPencilSimpleLine,
  PiPercent,
  PiRowsPlusBottom,
  PiScroll,
  PiSelectionPlus,
  PiShoppingBag,
  PiVideo,
} from 'react-icons/pi';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import {
  IContractSideMenuForm,
  IContractTemplateDetailItemForm,
  SIDE_MENU_TYPE,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import { ISize } from 'types/components/components.interface';
import {
  animateLayoutChanges,
  flattenTree,
  getChildCount,
} from 'utils/dnd/dndUtilities';
import './ContractInputWrapper.scss';

export interface IContractInputWrapperProps
  extends HTMLAttributes<HTMLDivElement>,
    ISize {
  id: string;
  depth: number;
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  onCollapse?(): void;
  disabledSelection?: boolean;
  handleProps?: any;
  onRemove?(): void;
  onAddSection?(): void;
  onAddCustomSection?(): void;
  onAddDigitalDistribution?(): void;
  onAddPhysicalDistribution?(): void;
  onAddMerchDistribution?(): void;
  onAddChannelDistribution?(): void;
  onAddCustomDigitalDistribution?(): void;
  onAddCustomPhysicalDistribution?(): void;
  onAddCustomMerchDistribution?(): void;
  onAddCustomChannelDistribution?(): void;
  onAddCustomCondition?(): void;
  onAddCondition?(): void;
  onAddCustomContract?(): void;
  onEditItem?(): void;
  onAddShare?(): void;
  children?: ReactNode;
}

const ContractInputWrapper = ({
  childCount,
  clone,
  depth,
  disabledSelection,
  handleProps,
  collapsed,
  onCollapse,
  onRemove,
  onAddSection,
  onAddDigitalDistribution,
  onAddPhysicalDistribution,
  onAddMerchDistribution,
  onAddChannelDistribution,
  onAddCustomCondition,
  onAddCustomDigitalDistribution,
  onAddCustomPhysicalDistribution,
  onAddCustomMerchDistribution,
  onAddCustomChannelDistribution,
  onAddCustomSection,
  onAddCondition,
  onAddCustomContract,
  onAddShare,
  onEditItem,
  children,
  size,
  id,
  ...props
}: IContractInputWrapperProps) => {
  const { indicator, values, setFieldValue, maxDepth, displayMode } =
    useFormikContractHelpers();

  const { applicationSettings } = useStoreState();

  const {
    attributes,
    isDragging,
    isSorting,
    listeners,
    setDraggableNodeRef,
    setDroppableNodeRef,
    transform,
    transition,
  } = useSortable({
    id,
    animateLayoutChanges,
  });

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const wrapperClassNames = cs(['contract-input-wrapper'], {
    clone: !!clone,
    ghost: !!isDragging,
    indicator: !!indicator,
    disableSelection: !!disabledSelection,
    disableInteraction: !!isSorting,
    sideMenuDisplayed:
      values.sideMenu?.displayed &&
      (values.sideMenu.detail?.id === id ||
        values.sideMenu.customTemplate?.parentId === id),
  });

  const collapseClassNames = cs(['Collapse'], {
    collapsed: collapsed,
  });

  const onDisplayDetail = () => {
    const item = flattenTree(values.items).find(item => item.id === id);
    const content = item?.type === TEMPLATE_TYPE.agreement && item.content;
    if (
      content &&
      values.sideMenu?.displayed &&
      content.type !== AGREEMENT_TYPE.shares &&
      content.type !== AGREEMENT_TYPE.defaultShare
    ) {
      const newDetail: IContractTemplateDetailItemForm = {
        id,
        content,
      };

      const sideMenu: IContractSideMenuForm = {
        type: SIDE_MENU_TYPE.agreementDetail,
        displayed: true,
        detail: newDetail,
      };

      setFieldValue('sideMenu', sideMenu);
    }
  };

  const getCanAddNestedSection = () => depth < maxDepth - 1;

  return (
    <div
      ref={setDroppableNodeRef}
      className='contract-input-depth'
      style={
        {
          '--depth': depth,
        } as React.CSSProperties
      }
      onClick={onDisplayDetail}>
      <div
        style={style}
        ref={setDraggableNodeRef}
        className={wrapperClassNames}
        {...props}>
        <div className='contract-input-wrapper-buttons'>
          {!displayMode && (
            <Button
              onlyIcon
              size={size}
              isClear
              disabled={disabledSelection}
              {...(disabledSelection ? {} : { ...attributes, ...listeners })}>
              <Button.Icon icon={<PiDotsSixVerticalBold />} />
            </Button>
          )}
          {onCollapse && (
            <Button
              onlyIcon
              isClear
              size='compact'
              onClick={onCollapse}
              wrapperClassNames={collapseClassNames}>
              <Button.Icon icon={<PiCaretDown />} />
            </Button>
          )}
        </div>
        <div className='contract-input-content'>{children ?? id}</div>
        <div className='contract-input-wrapper-buttons'>
          {!displayMode &&
            (onAddCondition ||
              onAddCustomCondition ||
              onAddSection ||
              onAddCustomSection ||
              onAddDigitalDistribution ||
              onAddCustomDigitalDistribution ||
              onAddPhysicalDistribution ||
              onAddCustomPhysicalDistribution ||
              onAddMerchDistribution ||
              onAddCustomMerchDistribution ||
              onAddChannelDistribution ||
              onAddCustomChannelDistribution ||
              onAddCustomContract ||
              onEditItem ||
              onAddShare ||
              onRemove) && (
              <Button
                className='show-on-hover'
                onlyIcon
                size={size}
                isClear
                onClick={handleComponentVisible}
                outsideChildren={
                  <DropdownMenu
                    ref={ref}
                    visible={isComponentVisible}
                    justifyPosition={'end'}
                    style={{ padding: '0' }}>
                    <div className='flex flex-col w-full'>
                      {!values.details && onEditItem && (
                        <Button
                          isClear
                          size={size}
                          fullWidth
                          onClick={() => {
                            onEditItem();
                            handleComponentVisible();
                          }}>
                          <Button.Icon icon={<PiPencilSimpleLine />} />
                          <Button.Text text={i18next.t('button.edit')} />
                        </Button>
                      )}
                      {onAddShare && (
                        <Button
                          isClear
                          size={size}
                          fullWidth
                          onClick={() => {
                            onAddShare();
                            handleComponentVisible();
                          }}>
                          <Button.Icon icon={<PiPercent />} />
                          <Button.Text text={i18next.t('button.share')} />
                        </Button>
                      )}
                      {onAddCondition && (
                        <Button
                          isClear
                          size={size}
                          fullWidth
                          onClick={() => {
                            onAddCondition();
                            handleComponentVisible();
                          }}>
                          <Button.Icon icon={<PiRowsPlusBottom />} />
                          <Button.Text
                            text={i18next.t(
                              'contract-template.agreement.title'
                            )}
                          />
                        </Button>
                      )}
                      {onAddDigitalDistribution && (
                        <Button
                          type='button'
                          isClear
                          size={size}
                          onClick={() => {
                            onAddDigitalDistribution();
                            handleComponentVisible();
                          }}
                          fullWidth>
                          <Button.Icon icon={<PiHeadphones />} />
                          <Button.Text
                            text={i18next.t(
                              'contract-template.digitalDistribution.title'
                            )}
                          />
                        </Button>
                      )}
                      {onAddPhysicalDistribution && (
                        <Button
                          type='button'
                          isClear
                          size={size}
                          onClick={() => {
                            onAddPhysicalDistribution();
                            handleComponentVisible();
                          }}
                          fullWidth>
                          <Button.Icon icon={<PiCassetteTape />} />
                          <Button.Text
                            text={i18next.t(
                              'contract-template.physicalDistribution.title'
                            )}
                          />
                        </Button>
                      )}
                      {onAddChannelDistribution && (
                        <Button
                          type='button'
                          isClear
                          size={size}
                          onClick={() => {
                            onAddChannelDistribution();
                            handleComponentVisible();
                          }}
                          fullWidth>
                          <Button.Icon icon={<PiVideo />} />
                          <Button.Text
                            text={i18next.t(
                              'contract-template.channelDistribution.title'
                            )}
                          />
                        </Button>
                      )}
                      {onAddMerchDistribution && (
                        <Button
                          type='button'
                          isClear
                          size={size}
                          onClick={() => {
                            onAddMerchDistribution();
                            handleComponentVisible();
                          }}
                          fullWidth>
                          <Button.Icon icon={<PiShoppingBag />} />
                          <Button.Text
                            text={i18next.t(
                              'contract-template.merchDistribution.title'
                            )}
                          />
                        </Button>
                      )}
                      {onAddSection && getCanAddNestedSection() && (
                        <Button
                          type='button'
                          isClear
                          size={size}
                          onClick={() => {
                            onAddSection();
                            handleComponentVisible();
                          }}
                          fullWidth>
                          <Button.Icon icon={<PiSelectionPlus />} />
                          <Button.Text
                            text={i18next.t('contract-template.section.title')}
                          />
                        </Button>
                      )}
                      {onAddCustomContract && getCanAddNestedSection() && (
                        <span className='dropdown-line' />
                      )}
                      {applicationSettings?.extensionContract === true && (
                        <>
                          {onAddCustomCondition && (
                            <Button
                              isClear
                              size={size}
                              fullWidth
                              onClick={() => {
                                onAddCustomCondition();
                                handleComponentVisible();
                              }}>
                              <Button.Icon icon={<PiRowsPlusBottom />} />
                              <Button.Text
                                text={i18next.t(
                                  'contract-template.agreement.custom'
                                )}
                              />
                            </Button>
                          )}

                          {onAddCustomDigitalDistribution && (
                            <Button
                              isClear
                              size={size}
                              fullWidth
                              onClick={() => {
                                onAddCustomDigitalDistribution();
                                handleComponentVisible();
                              }}>
                              <Button.Icon icon={<PiHeadphones />} />
                              <Button.Text
                                text={i18next.t(
                                  'contract-template.digitalDistribution.custom'
                                )}
                              />
                            </Button>
                          )}
                          {onAddCustomPhysicalDistribution && (
                            <Button
                              isClear
                              size={size}
                              fullWidth
                              onClick={() => {
                                onAddCustomPhysicalDistribution();
                                handleComponentVisible();
                              }}>
                              <Button.Icon icon={<PiCassetteTape />} />
                              <Button.Text
                                text={i18next.t(
                                  'contract-template.physicalDistribution.custom'
                                )}
                              />
                            </Button>
                          )}
                          {onAddCustomChannelDistribution && (
                            <Button
                              isClear
                              size={size}
                              fullWidth
                              onClick={() => {
                                onAddCustomChannelDistribution();
                                handleComponentVisible();
                              }}>
                              <Button.Icon icon={<PiVideo />} />
                              <Button.Text
                                text={i18next.t(
                                  'contract-template.channelDistribution.custom'
                                )}
                              />
                            </Button>
                          )}
                          {onAddCustomMerchDistribution && (
                            <Button
                              isClear
                              size={size}
                              fullWidth
                              onClick={() => {
                                onAddCustomMerchDistribution();
                                handleComponentVisible();
                              }}>
                              <Button.Icon icon={<PiShoppingBag />} />
                              <Button.Text
                                text={i18next.t(
                                  'contract-template.merchDistribution.custom'
                                )}
                              />
                            </Button>
                          )}
                          {onAddCustomSection && depth < maxDepth - 1 && (
                            <Button
                              isClear
                              size={size}
                              onClick={() => {
                                onAddCustomSection();
                                handleComponentVisible();
                              }}
                              fullWidth>
                              <Button.Icon icon={<PiSelectionPlus />} />
                              <Button.Text
                                text={i18next.t(
                                  'contract-template.section.custom'
                                )}
                              />
                            </Button>
                          )}
                          {(onAddCustomCondition ||
                            onAddCustomDigitalDistribution ||
                            onAddCustomChannelDistribution ||
                            onAddCustomMerchDistribution ||
                            onAddCustomPhysicalDistribution ||
                            (onAddCustomSection &&
                              getCanAddNestedSection())) && (
                            <span className='dropdown-line' />
                          )}
                        </>
                      )}
                      {onAddCustomContract && (
                        <Button
                          isClear
                          size={size}
                          onClick={() => {
                            onAddCustomContract();
                            handleComponentVisible();
                          }}
                          fullWidth>
                          <Button.Icon icon={<PiScroll />} />
                          <Button.Text
                            text={i18next.t(
                              'contract-template.contract.custom'
                            )}
                          />
                        </Button>
                      )}
                      {onRemove && (
                        <>
                          {(onAddCondition ||
                            onAddSection ||
                            onAddDigitalDistribution ||
                            (!values.details ? onEditItem : false)) && (
                            <span className='dropdown-line' />
                          )}
                          {/* {(onAddCondition ||
                            onAddCustomCondition ||
                            onAddSection ||
                            onAddCustomSection ||
                            onAddDigitalDistribution ||
                            onAddCustomDigitalDistribution ||
                            (!values.details ? onEditItem : false)) && (
                            <span className='dropdown-line' />
                          )} */}
                          <Popup.RemoveContractItem
                            onRemove={actions => {
                              actions !== null && actions.close();
                              onRemove();
                            }}
                            itemCount={getChildCount(values.items, id)}
                            size={size}
                          />
                        </>
                      )}
                    </div>
                  </DropdownMenu>
                }>
                <Button.Icon icon={<PiDotsThreeVerticalBold />} />
              </Button>
            )}
        </div>
        {(clone || collapsed) && childCount && childCount > 1 ? (
          <span
            className={`Count ${!clone && collapsed ? 'count-collapsed' : ''}`}>
            {childCount}
          </span>
        ) : null}
      </div>
    </div>
  );
};
export default ContractInputWrapper;
